import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { ChevronIcon } from "@theme/assets";
import { createElement, type FC } from "react";

import { Box, Button } from "@theme/components";

import { type MsComponentPropsWithoutRef } from "@theme/types/global";

const AccordionRoot: FC<MsComponentPropsWithoutRef<typeof AccordionPrimitive.Root>> = ({
  sx,
  ...props
}) => <AccordionPrimitive.Root data-slot="accordion" multiple={false} sx={sx} {...props} />;

const AccordionItem: FC<MsComponentPropsWithoutRef<typeof AccordionPrimitive.Item>> = ({
  sx,
  ...props
}) => (
  <AccordionPrimitive.Item
    data-slot="accordion-item"
    sx={{ position: "relative", bg: "inherit", py: "s200", ...sx }}
    {...props}
  />
);

interface AccordionTriggerProps extends MsComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> {
  headingLevel?: 2 | 3 | 4 | 5 | 6;
}

const AccordionTrigger: FC<AccordionTriggerProps> = ({ sx, children, headingLevel, ...props }) => (
  <AccordionPrimitive.Header
    data-slot="accordion-header"
    render={headingLevel === undefined ? undefined : createElement(`h${headingLevel}`)}
    sx={{
      // no global CSS reset in this repo — raw h3 from Base UI keeps browser margins
      m: 0,
      "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "1px",
        bg: "borderLevel3",
        pointerEvents: "none",
      },
      "&:hover": {
        "[data-slot='accordion-title'] > p": {
          color: "textLevel2",
        },
        ":after": {
          bg: "borderLevel2",
        },
      },
    }}
  >
    <AccordionPrimitive.Trigger
      data-slot="accordion-trigger"
      render={
        <Button
          variant="ghost"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "full",
            textAlign: "left",
            py: "s400",
            pl: 0,
            ".keyboard-user &:focus": {
              ":before": {
                m: 0,
              },
            },
            "&[data-panel-open] [data-slot='accordion-chevron']": {
              color: "textBrand",
              transform: "translateY(4px) rotate(-90deg)",
            },
            ...sx,
          }}
        />
      }
      {...props}
    >
      <Box data-slot="accordion-title" sx={{ maxWidth: "content05" }}>
        {children}
      </Box>
      <ChevronIcon
        data-slot="accordion-chevron"
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: [18, null, null, 22],
          height: [18, null, null, 22],
          color: "textBrandLevel2",
          "@media (prefers-reduced-motion: no-preference)": {
            transition: "transform 0.2s ease-in-out",
          },
          transform: "translateY(4px) rotate(-270deg)",
          ml: "s400",
        }}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);

const AccordionPanel: FC<MsComponentPropsWithoutRef<typeof AccordionPrimitive.Panel>> = ({
  sx,
  ...props
}) => (
  <AccordionPrimitive.Panel
    data-slot="accordion-panel"
    sx={{
      overflow: "hidden",
      // overflow:hidden (for the height animation) clips focus rings; the
      // padding gives them room and the negative margin cancels it visually.
      p: "s200",
      m: "-s200",
      "@media (prefers-reduced-motion: no-preference)": {
        transition: "height 300ms cubic-bezier(0.33, 1, 0.68, 1), opacity 300ms ease-out",
      },
      height: "var(--accordion-panel-height)",
      opacity: 1,
      "&[data-starting-style], &[data-ending-style]": {
        height: 0,
        opacity: 0,
      },
      ...sx,
    }}
    {...props}
  />
);

export { AccordionItem, AccordionPanel, AccordionRoot, AccordionTrigger };
export type { AccordionTriggerProps };
